import { axiosInstanceTasks } from "../axios";
import { Historys, RequestResponse } from "../types";

export const getHistory = async (
  taskName: string,
  pageNumber: number,
  pageSize: number,
  status?: string,
  dateFrom?: string,
  dateTo?: string,
  dateFilterColumn?: string
): Promise<RequestResponse<{ records: Historys[]; total: number }>> => {
  try {
    const queryParams = {
      taskName: encodeURIComponent(taskName),
      page: pageNumber,
      size: pageSize,
      status: status || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      dateFilterColumn: dateFilterColumn || undefined
    };

    const response = await axiosInstanceTasks.get("/task-histories-paginated", {
      params: queryParams
    });

    return {
      error: false,
      data: {
        records: response.data.data.records,
        total: response.data.data.total
      }
    };
  } catch (err) {
    console.error("Unable to fetch task: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: null
    };
  }
};

export const downloadFile = async (
  id: number,
  taskName: string | null = null
): Promise<RequestResponse> => {
  try {
    const response = await axiosInstanceTasks.get(
      `/file-histories/${id}/download`,
      {
        responseType: "blob"
      }
    );

    const contentDisposition = response.headers["content-disposition"];

    let fileName = taskName ? `${taskName}_${id}` : `file_history_${id}`;

    if (contentDisposition) {
      const [_, newFileName] =
        contentDisposition.match(/filename="?([^"]*)"?/) || [];

      if (newFileName) {
        fileName = newFileName;
      }
    }

    if (fileName && !fileName.toLowerCase().endsWith(".zip")) {
      fileName = `${fileName}.zip`;
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      error: false,
      data: null,
      message: `Successfully downloaded file: ${fileName}`
    };
  } catch (err) {
    console.error("Unable to download file: ", err);

    let errorMessage =
      "File download failed. The record may not exist or the file may be missing.";
    const errorResponse = err.response;

    if (errorResponse && errorResponse.data instanceof Blob) {
      try {
        const blobText = await errorResponse.data.text();
        const errorJson = JSON.parse(blobText);

        errorMessage = errorJson.message || errorMessage;
      } catch (e) {
        console.warn(
          "Could not parse error response as JSON. Falling back to generic message."
        );
      }
    } else {
      errorMessage =
        errorResponse?.data?.error_description ||
        errorResponse?.data?.message ||
        err.message ||
        errorMessage;
    }

    if (errorMessage.includes("Access is denied")) {
      errorMessage =
        "Access denied: You do not have permission to perform this activity.";
    }

    return {
      error: true,
      message: errorMessage,
      data: errorResponse?.data?.data || null
    };
  }
};
